import { useMemo, useState } from "react";
import { Bell, RotateCcw } from "lucide-react";
import { UpgradePrompt } from "../../components/billing/UpgradePrompt";
import { Switch } from "../../components/ui/switch";
import { useGroupTier } from "../../hooks/useGroupTier";
import {
  UPCOMING_REMINDER_OFFSETS_MINUTES,
  VOTE_REMINDER_OFFSETS_MINUTES,
  createReminderSettingsSnapshot,
} from "./reminder-settings";
import { ReminderTimingControl } from "./ReminderTimingControl";

function LaneToggle({ id, label, checked, disabled, onChange }) {
  return (
    <label
      htmlFor={id}
      className={`flex min-h-[40px] items-center gap-2 rounded-xl border border-border bg-card px-3 text-xs font-semibold text-card-foreground ${
        disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
      }`}
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="h-4 w-4 rounded border-input accent-brand-primary"
      />
      {label}
    </label>
  );
}

function relevantKindKeys(scope) {
  if (scope === "basic" || scope === "embedded") return ["basicPollVotes"];
  return ["sessionVotes", "basicPollVotes", "upcomingSessions"];
}

export function PollNotificationSettings({
  idPrefix,
  group,
  value,
  inheritedValue,
  onChange,
  scope = "session",
  inheritedLabel = "questing group defaults",
}) {
  const [error, setError] = useState("");
  const inherited = useMemo(
    () => createReminderSettingsSnapshot(inheritedValue, {
      discordLinked: Boolean(group?.discord?.channelId),
    }),
    [group?.discord?.channelId, inheritedValue]
  );
  const settings = useMemo(
    () => createReminderSettingsSnapshot(value, {
      discordLinked: Boolean(group?.discord?.channelId),
    }),
    [group?.discord?.channelId, value]
  );
  const customized = JSON.stringify(settings) !== JSON.stringify(inherited);
  const tier = useGroupTier(group?.creatorId || null);
  const entitled = Boolean(group) && !tier.loading && tier.canUseFeature("AUTOMATED_REMINDERS");
  const canEdit = Boolean(group) && (tier.loading || entitled);
  const kindKeys = relevantKindKeys(scope);

  const update = (next) => {
    setError("");
    onChange(createReminderSettingsSnapshot(next, {
      discordLinked: Boolean(group?.discord?.channelId),
    }));
  };

  const updateKind = (key, nextKind) => update({ ...settings, [key]: nextKind });
  const updateChannel = (key, checked) => {
    const channels = { ...settings.channels, [key]: checked };
    if (settings.enabled && !Object.values(channels).some(Boolean)) {
      setError("Keep at least one delivery option while reminders are on.");
      return;
    }
    update({ ...settings, channels });
  };

  return (
    <section className="rounded-md border border-violet-200 bg-violet-50/50 p-3 dark:border-violet-800 dark:bg-violet-950/20">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 gap-2.5">
          <span className="mt-0.5 rounded-lg bg-violet-100 p-2 text-violet-700 dark:bg-violet-900/50 dark:text-violet-200">
            <Bell className="h-4 w-4" aria-hidden="true" />
          </span>
          <div>
            <p className="text-xs font-semibold text-card-foreground">Notifications</p>
            <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">
              {customized
                ? `Customized for this poll. ${inheritedLabel[0].toUpperCase()}${inheritedLabel.slice(1)} stay unchanged.`
                : `Pre-filled from ${inheritedLabel}. Changes here affect only this poll.`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {customized ? (
            <button
              type="button"
              onClick={() => update(inherited)}
              disabled={!canEdit}
              className="inline-flex min-h-[36px] items-center gap-1.5 rounded-full px-2.5 text-[11px] font-semibold text-violet-700 hover:bg-violet-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 dark:text-violet-200 dark:hover:bg-violet-900/40"
            >
              <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
              Reset to defaults
            </button>
          ) : null}
          <Switch
            id={`${idPrefix}-reminders-enabled`}
            checked={settings.enabled}
            onCheckedChange={(enabled) => update({
              ...settings,
              enabled,
              channels:
                enabled && !Object.values(settings.channels).some(Boolean)
                  ? { ...settings.channels, inApp: true }
                  : settings.channels,
            })}
            disabled={!canEdit}
            aria-label="Automatic notifications for this poll"
          />
        </div>
      </div>

      {!group ? (
        <p className="mt-3 rounded-lg bg-card px-3 py-2 text-xs text-muted-foreground">
          Choose a questing group to configure automatic notifications.
        </p>
      ) : !tier.loading && !entitled ? (
        <div className="mt-3">
          <UpgradePrompt
            feature="AUTOMATED_REMINDERS"
            tier="lite"
            message="Automatic poll notifications require Lite for the questing group owner."
          />
        </div>
      ) : null}

      {settings.enabled && group ? (
        <div className="mt-3 space-y-3">
          {kindKeys.includes("sessionVotes") ? (
            <ReminderTimingControl
              id={`${idPrefix}-session-votes`}
              title="Session voting deadline"
              description="Remind current participants who have not submitted a session vote. A deadline is required."
              value={settings.sessionVotes}
              allowedOffsets={VOTE_REMINDER_OFFSETS_MINUTES}
              disabled={!canEdit}
              onChange={(next) => updateKind("sessionVotes", next)}
              onConstraint={setError}
            />
          ) : null}
          {kindKeys.includes("basicPollVotes") ? (
            <ReminderTimingControl
              id={`${idPrefix}-basic-votes`}
              title={scope === "session" ? "Required add-on poll deadlines" : "Poll voting deadline"}
              description={scope === "session"
                ? "Remind current non-voters before deadlines on required add-on polls."
                : "Remind current group members who have not submitted a vote. A deadline is required."}
              value={settings.basicPollVotes}
              allowedOffsets={VOTE_REMINDER_OFFSETS_MINUTES}
              disabled={!canEdit}
              onChange={(next) => updateKind("basicPollVotes", next)}
              onConstraint={setError}
            />
          ) : null}
          {kindKeys.includes("upcomingSessions") ? (
            <ReminderTimingControl
              id={`${idPrefix}-upcoming-session`}
              title="Upcoming finalized session"
              description="Remind participants before the finalized session's winning slot starts."
              value={settings.upcomingSessions}
              allowedOffsets={UPCOMING_REMINDER_OFFSETS_MINUTES}
              timingLabel="Before the session starts"
              disabled={!canEdit}
              onChange={(next) => updateKind("upcomingSessions", next)}
              onConstraint={setError}
            />
          ) : null}

          <fieldset>
            <legend className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Deliver through
            </legend>
            <div className="mt-2 grid gap-2 sm:grid-cols-3">
              <LaneToggle
                id={`${idPrefix}-lane-in-app`}
                label="In-app"
                checked={settings.channels.inApp}
                disabled={!canEdit}
                onChange={(checked) => updateChannel("inApp", checked)}
              />
              <LaneToggle
                id={`${idPrefix}-lane-email`}
                label="Email"
                checked={settings.channels.email}
                disabled={!canEdit}
                onChange={(checked) => updateChannel("email", checked)}
              />
              <LaneToggle
                id={`${idPrefix}-lane-discord`}
                label="Discord"
                checked={settings.channels.discord}
                disabled={!canEdit || !group?.discord?.channelId}
                onChange={(checked) => updateChannel("discord", checked)}
              />
            </div>
            {!group?.discord?.channelId ? (
              <p className="mt-1.5 text-[11px] text-amber-700 dark:text-amber-300">
                Link a Discord channel in the group settings to use that lane.
              </p>
            ) : null}
          </fieldset>
        </div>
      ) : null}

      {error ? (
        <p role="alert" className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-950/40 dark:text-red-200">
          {error}
        </p>
      ) : null}
    </section>
  );
}
